import { useState, useEffect, useRef } from "react";

import { downloadAuthenticatedFile, makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import { BannerNotificationType } from "../../components/SimpleNotification";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import ProgressModal from "../../components/modals/ProgressModal";

export function QaReportsViewModel(logout) {
  const defaultFilters = {
    class: { value: -1, label: "All classes" },
    category: { value: -1, label: "All categories" },
    product: { value: -1, label: "All products" },
    supplier: { value: -1, label: "All suppliers" },
    weighting_user_id: { value: -1, label: "All weighing users" },
    startDate: null,
    endDate: null,
  };
  const qaReportOptions = [
    { value: 5, label: "Deleted Cartons" },
    { value: 6, label: "Inventory Valuation" },
    { value: 8, label: "Flow Reports" },
    { value: 7, label: "Open WIPs Report" },
    { value: 9, label: "Production Detail" },
    { value: 10, label: "Production Summary" },
    { value: 1, label: "Receiving Details" },
    { value: 2, label: "Receiving Summary" },
    { value: 3, label: "Reorder Limit Items" },
    { value: 11, label: "Sales Comparison Report" },
    { value: 4, label: "Sales Report by PLU" },
    { value: 12, label: "Employee Weighing Report" },
  ];
  const [filters, setFilters] = useState(defaultFilters);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState({
    value: -1,
    label: "Select report",
  });
  const [showSimpleNotification, setShowSimpleNotification] = useState({
    show: false,
    message: null,
    title: null,
    type: BannerNotificationType.Success,
  });
  const [categories, setCategories] = useState([]);
  const [classes, setClasses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [weighingUsers, setWeighingUsers] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [recentReportsLoading, setRecentReportsLoading] = useState(true);
  const pollingIds = useRef(new Set());
  const mounted = useRef(true);

  useEffect(() => {
    const activePollingIds = pollingIds.current;
    mounted.current = true;
    return () => {
      mounted.current = false;
      activePollingIds.clear();
    };
  }, []);

  const showProgressModal = (message) => {
    setEmptyModelOpen({
      open: true,
      childComponent: <ProgressModal message={message} />,
    });
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const getCategoryTypes = async function () {
    if (filters.class.value === -1) {
      return;
    }
    let callResult = await makeGetApiCall(
      `categoriesForDropdownWithClass/${filters.class.value}`
    );
    if (callResult.success) {
      setCategories([
        { value: -1, label: "All categories" },
        ...callResult.data,
      ]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch category types");
    }
  };

  useEffect(() => {
    if (filters.class.value === -1) {
      setCategories([{ value: -1, label: "All categories" }]);
    } else {
      getCategoryTypes();
    }
  }, [filters.class.value]);

  const getClassTypes = async function () {
    let callResult = await makeGetApiCall("classesForDropdown");
    if (callResult.success) {
      setClasses([{ value: -1, label: "All classes" }, ...callResult.data]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getCustomersForDropdown = async function () {
    let callResult = await makeGetApiCall(`customersForDropdown`);
    if (callResult.success) {
      setCustomers([{ value: -1, label: "All customers" }, ...callResult.data]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getProductOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`productsForDropdown`);

    if (callResult.success) {
      setProducts([{ value: -1, label: "All products" }, ...callResult.data]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getSupplierOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`suppliersForDropdown`);

    if (callResult.success) {
      setSuppliers([{ value: -1, label: "All suppliers" }, ...callResult.data]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch suppliers");
    }
  };

  const getWeighingUsersForDropdown = async function () {
    let callResult = await makeGetApiCall("icapWeightingUsersForDropdown");
    if (callResult.success) {
      setWeighingUsers([
        { value: -1, label: "All weighing users" },
        ...callResult.data,
      ]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal("Could not fetch weighing users");
      }
    }
  };

  const printSelectedReport = async function () {
    if (reportType.value === -1) {
      showErrorModal("Please select a report type");
      return;
    }
    if (reportType.value !== 7 && (!filters.startDate || (reportType.value !== 6 && !filters.endDate))) {
      showErrorModal("Please select a date range");
      return;
    }
    if (reportType.value === 11 && (!filters.startDate2 || !filters.endDate2)) {
      showErrorModal("Please select both comparison date ranges");
      return;
    }
    if (reportType.value === 12 && filters.weighting_user_id?.value === -1) {
      showErrorModal("Please select a weighing user", "A weighing user is required for the Employee Weighing Report.");
      return;
    }
    showProgressModal("Submitting report...");
    const reportUrl = (() => {
      switch (reportType.value) {
        case 1:
          return "generateGeneralReceivingDetailsReport";
        case 2:
          return "generateGeneralReceivingSummaryReport";
        case 3:
          return "reorderLimitReport";
        case 4:
          return "salesReportByPlu";
        case 5:
          return "deletedCartonsReport";
        case 6:
          return "inventoryValuationReport";
        case 7:
          return "openWipsReport";
        case 8:
          return "inventoryFlow";
        case 9:
          return "productionDetailsReport";
        case 10:
          return "productionSummaryReport";
        case 11:
          return "salesComparisonReport";
        case 12:
          return "employeeWeighingReport";
        default:
          return "error_type";
      }
    })();

    const reportTypeKey = {
      1: "receiving_details",
      2: "receiving_summary",
      3: "reorder_limit",
      4: "sales_by_plu",
      5: "deleted_cartons",
      6: "inventory_valuation",
      7: "open_wips",
      8: "inventory_flow",
      9: "production_details",
      10: "production_summary",
      11: "sales_comparison",
      12: "employee_weighing",
    }[reportType.value];
    const parameters = {
      ...filters,
      endDate: reportType.value === 6 ? filters.startDate : filters.endDate,
    };

    setLoading(true);
    let callResult = await makePostApiCall("qa-report-requests", {
      report_type: reportTypeKey,
      parameters,
    });

    // Allows the frontend to be deployed before the queued-report backend.
    if (!callResult.success && callResult.errorStatus === 404) {
      callResult = await makePostApiCall(reportUrl, { filter: parameters });
    }

    if (callResult.success) {
      if (callResult.data.status_url) {
        upsertReport(callResult.data);
        await pollReport(callResult.data, true);
      } else {
        window.open(callResult.data.location, "_blank");
        setEmptyModelOpen({ open: false, childComponent: null });
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      setEmptyModelOpen({ open: false, childComponent: null });
      showErrorModal(callResult.data.message);
    }

    setLoading(false);

    return callResult;
  };

  const upsertReport = (report) => {
    if (!mounted.current) return;
    setRecentReports((current) => {
      const index = current.findIndex((item) => item.id === report.id);
      if (index === -1) return [report, ...current];
      return current.map((item) => (item.id === report.id ? report : item));
    });
  };

  const downloadReport = async (report) => {
    if (!report.download_url) return;
    const result = await downloadAuthenticatedFile(
      report.download_url,
      report.output_name || `${report.report_name || "report"}.pdf`,
    );
    if (!result.success) {
      if (result.errorStatus === 401) logout();
      showErrorModal(result.data?.message || "Could not download report");
    }
  };

  const pollReport = async (initialReport, foreground = false) => {
    if (pollingIds.current.has(initialReport.id) && !foreground) return;
    pollingIds.current.add(initialReport.id);
    let report = initialReport;

    try {
      while (mounted.current && ["queued", "processing"].includes(report.status)) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        if (!mounted.current) return;
        const result = await makeGetApiCall(report.status_url);
        if (!result.success) {
          if (result.errorStatus === 401) logout();
          if (foreground) {
            setEmptyModelOpen({ open: false, childComponent: null });
            showErrorModal(result.data?.message || "Could not check report status");
          }
          return;
        }
        report = result.data;
        upsertReport(report);
      }

      if (foreground && mounted.current) {
        setEmptyModelOpen({ open: false, childComponent: null });
        if (report.status === "completed") {
          await downloadReport(report);
        } else if (report.status === "failed") {
          showErrorModal("Report generation failed", report.error_message);
        }
      }
    } finally {
      pollingIds.current.delete(initialReport.id);
    }
  };

  const getRecentReports = async () => {
    setRecentReportsLoading(true);
    const result = await makeGetApiCall("qa-report-requests?per_page=50");
    if (result.success) {
      const reports = result.data.data || [];
      if (mounted.current) setRecentReports(reports);
      reports
        .filter((report) => ["queued", "processing"].includes(report.status))
        .forEach((report) => pollReport(report));
    } else if (result.errorStatus === 401) {
      logout();
    }
    if (mounted.current) setRecentReportsLoading(false);
  };

  const retryReport = async (report) => {
    showProgressModal("Submitting report again...");
    const result = await makePostApiCall(`qa-report-requests/${report.id}/retry`, {});
    if (!result.success) {
      setEmptyModelOpen({ open: false, childComponent: null });
      if (result.errorStatus === 401) logout();
      showErrorModal(result.data?.message || "Could not generate report again");
      return;
    }
    upsertReport(result.data);
    await pollReport(result.data, true);
  };

  function resetFilters() {
    setFilters(defaultFilters);
  }
  return {
    emptyModal,
    loading,
    setEmptyModelOpen,
    setShowSimpleNotification,
    showSimpleNotification,
    filters,
    printSelectedReport,
    qaReportOptions,
    setFilters,
    reportType,
    setReportType,
    resetFilters,
    categories,
    products,
    suppliers,
    getCategoryTypes,
    getCustomersForDropdown,
    getProductOptionsForDropdown,
    getSupplierOptionsForDropdown,
    classes,
    getClassTypes,
    weighingUsers,
    getWeighingUsersForDropdown,
    recentReports,
    recentReportsLoading,
    getRecentReports,
    downloadReport,
    retryReport,
  };
}
